import React from 'react';
import { IPropertyChild, getNodeEditorName } from 'components/property-editors/utils/ProviderNodeUtils';
import { EditorMap } from './EditorMap';
import { IPropertyChangeHandler } from 'components/property-editors/PropertyGrid';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { Label } from '../inputs/Label';
import { LegacyEditor, LegacyEditorList } from './legacy-editors/LegacyEditor';

export interface IEditorWrapperProps {
  /**
   * Property child data to render.
   */
  child: IPropertyChild;
  /**
   * ID / key of the rendered node. Also probably used to track
   * some kind of model updates. Derived from the node name and/or depth if not provided.
   */
  id?: string;
  /**
   * How to handle these state updates is kind of a key challenge
   * in this exercise - marked as optional since it's being provided by
   * context within EditorChild component
   */
  onChange?: IPropertyChangeHandler;

  depth?: number;

  hideInputLabel?: boolean;

  path: string[];

  /**
   * `true` if the editor should trigger a dialog handler on it's first render.
   * Take care not to create a loop with this in editor impl.
   *
   * Currently only used in DataDescriptionPathEditor,
   */
  showDialogOnRender?: boolean;
  /**
   * Should the editor auto-focus when it is first rendered?
   */
  autoFocus?: boolean;
}

export interface IEditorProps extends IEditorWrapperProps {
  /**
   * Override as mandatory to avoid certain kinds of type errors
   */
  onChange: IPropertyChangeHandler;
}

interface IEditorState {
  hasError: boolean;
}

/* this could probably also house some hooks for updating state / */

/* or this could become the component that renders the others */

/**
 * A generic component for "Properties" __type child component.
 *
 * This is an entry-point component to render all other editor types.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export class Editor extends React.Component<IEditorWrapperProps, IEditorState> {
  context!: React.ContextType<typeof PropertyEditorContext>;

  constructor(props: IEditorWrapperProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: React.ErrorInfo) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
    console.log(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <h4>Something went wrong...</h4>;
    }

    if (!this.props.child) {
      return null;
    }
    const childProps = {
      ...this.props,
      onChange: this.context.onChange,
    };

    const editorName = getNodeEditorName(this.props.child.Node);
    const EditorComponent = EditorMap[editorName];

    if (DEV_BUILD && !LegacyEditorList.includes(editorName)) {
      // leave this warning out of production builds:
      !EditorComponent && console.warn(`${editorName} is not implemented yet`);
    }

    // a MappingTypeSelector is in fact a category with a MappingTypeSelector first-child and a second child
    // of whatever editor type. If we end up here, it's because some data was fragmented in the back-end,
    // and rendering nothing would preferable to rendering the "Impl Pending" message
    const isMappingTypeSelectorEdgeCase = this.props.child.Node.MvcEditorName === 'MappingTypeSelector';

    if (isMappingTypeSelectorEdgeCase) {
      console.warn(
        'Encountered MappingTypeSelector alone as an "editor". This is not supported, and is either a bug or a known corner case.',
        {
          path: this.props.path,
          node: this.props.child,
        }
      );
    }

    const isKnownEdgeCase = isMappingTypeSelectorEdgeCase;

    return EditorComponent ? (
      <EditorComponent {...childProps} />
    ) : LegacyEditorList.includes(editorName) ? (
      <LegacyEditor {...childProps} />
    ) : isKnownEdgeCase ? null : (
      <Label label={this.props.child.Node.DisplayLabel}>
        Impl Pending:
        <ul title="Can render legacy grid in a pinch by mapping MvcEditorName to LegacyEditor">
          <li>MvcEditorName: {this.props.child.Node.MvcEditorName}</li>
        </ul>
      </Label>
    );
  }
}

Editor.contextType = PropertyEditorContext;

export function getChildId(child: IPropertyChild, depth = 0) {
  return child.Node.Name + depth;
}
